'use strict';

import { hot } from 'shared/hot'; // Must be required before React
import React, { useState } from 'react';
import route from 'shared/ziggy.js';
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faComments, faDesktop, faSignOut, faStar, faToolbox, faUserSecret } from "@fortawesome/pro-regular-svg-icons";
import { postJson } from 'shared/fetch.js';
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil.js';

function AuthPanel(props) {
	if (props.loading) {
		return (
			<div className="m-4 antialiased">
				Loading…
			</div>
		);
	}
	
	return props.user
		? <UserPanel { ...props } />
		: <LoginPanel { ...props } />;
}

export function LoginPanel({ csrf, current_url }) {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [remember, setRemember] = useState(false);
	const [error, setError] = useState(null);
	const [status, setStatus] = useState('idle');
	
	let button_text = 'Log In';
	if ('logging-in' === status) {
		button_text = 'Please wait...';
	} else if ('logged-in' === status) {
		button_text = 'Logging in...';
	}
	
	const onSubmit = e => {
		e.preventDefault();
		
		setStatus('logging-in');
		
		const payload = { username, password };
		
		if (remember) {
			payload.remember = '1';
		}
		
		postJson(route('POST login'), payload)
			.then(res => {
				if (res.redirected) {
					return Promise.resolve({
						ok: true,
						destination: res.url,
					});
				}
				
				return res.json();
			})
			.then(response => {
				if (response.ok) {
					window.location.href = response.data.destination;
					setStatus('logged-in');
					setError(null);
				} else {
					setStatus('idle');
					
					if ('username' in response.errors) {
						setError(response.errors.username[0]);
					} else if ('password' in response.errors) {
						setError(response.errors.password[0]);
					} else {
						setError(`Invalid username or password.`);
					}
				}
			});
	};
	
	return (
		<div className="m-4 antialiased text-left">
			<form action={ route('POST login') } method="post" onSubmit={ onSubmit }>
				<input type="hidden" name="_token" value={ csrf } />
				<input type="hidden" name="redirect" value={ current_url } />
				
				<div className="mb-4 text-lg text-center text-gray-400 font-condensed" style={ { display: error ? 'none' : 'block' } }>
					Log into your account
				</div>
				
				<div className="alert alert-red" style={ { display: error ? 'block' : 'none' } }>
					{ error }
				</div>
				
				<div className="mb-4">
					<label className="inline-block mb-1 font-semibold" htmlFor="navbar--login-username">
						Your username or email:
					</label>
					<input
						value={ username }
						onChange={ e => setUsername(e.target.value) }
						type="text"
						name="username"
						id="navbar--login-username"
						className="block w-full p-2 text-base leading-normal text-gray-700 bg-white border border-gray-300 rounded focus:outline-none focus:shadow-outline"
					/>
				</div>
				
				<div className="mb-4">
					<label className="inline-block mb-1 font-semibold" htmlFor="navbar--login-password">
						Your password:
					</label>
					<input
						value={ password }
						onChange={ e => setPassword(e.target.value) }
						type="password"
						name="password"
						id="navbar--login-password"
						className="block w-full p-2 text-base leading-normal text-gray-700 bg-white border border-gray-300 rounded focus:outline-none focus:shadow-outline"
					/>
				</div>
				
				<div className="mb-4">
					<label>
						<input
							type="checkbox"
							name="remember"
							checked={ remember }
							onChange={ e => setRemember(e.target.checked) }
							id="navbar--login-remember"
						/>
						<span className="inline-block ml-1">Remember me</span>
					</label>
				</div>
				
				<div className="flex items-center justify-between mt-8">
					<button className={ `btn font-bold ${ 'logging-in' === status ? 'btn-gray' : 'btn-green' }` } type="submit">
						{ button_text }
					</button>
					<a className={ `${ 'idle' === status ? '' : 'hidden' } text-sm font-condensed text-blue-300 hover:text-blue-500` } href={ route('forgot-password') }>
						Forgot your password?
					</a>
				</div>
			</form>
			
			<div className="pt-6 mt-6 text-sm text-center text-gray-600 border-t">
				Not a member yet? <a href={ route('join') }>Sign up.</a>
			</div>
		</div>
	);
}

export function UserPanel({ user, admin_content, article_key, csrf }) {
	const { given_name, is_paying_member, is_all_access, is_staff, role, photo, display_name, impersonator_id } = user;
	
	return (
		<div className="m-4 text-left">
			<div className="flex items-center mb-8">
				<a href={ route('my.dashboard.index') }
				   style={ {
					   background: `url(${ photo }) no-repeat center center`,
					   width: 80,
					   height: 80,
				   } }
				   className="bg-cover border rounded-full hover:border-blue-500"
				/>
				<div className="pl-4">
					<div className="text-lg font-bold font-condensed">
						{ display_name }
					</div>
					<div className="mt-px">
						<span className="px-4 py-1 text-xs text-gray-700 bg-gray-200 rounded-full">
							{ role }
						</span>
					</div>
				</div>
			</div>
			<ul className="antialiased text-left list-reset">
				{ false === is_paying_member && (
					<li className="my-2 text-lg font-condensed">
						<a href={ route('my.billing') } className="block text-gray-600 no-underline hover:text-blue">
							<Icon icon={ faStar } className="inline mr-2 svg-fw" />
							Upgrade Account
						</a>
					</li>
				) }
				<li className="my-2 text-lg font-condensed">
					<a href={ route('my.dashboard.index') } className="block text-gray-600 no-underline hover:text-blue">
						<Icon icon={ faDesktop } className="inline mr-2 svg-fw" />
						My Dashboard
					</a>
				</li>
				{ (is_all_access || is_staff) && (
					<li className="my-2 text-lg font-condensed">
						<a href="https://forum.nachi.org/c/members-only" className="block text-gray-600 no-underline hover:text-blue">
							<Icon icon={ faComments } className="inline mr-2 svg-fw" />
							Members-Only Forum
						</a>
					</li>
				) }
				{ is_staff && (
					<li className="pt-2 my-2 mt-4 border-t">
						
						<a href={ route('admin.dashboards.index') } className="block my-2 text-lg text-gray-600 no-underline font-condensed hover:text-blue">
							<Icon icon={ faToolbox } className="inline mr-2 svg-fw" />
							Admin Dashboard
						</a>
						
						{ article_key && (
							<a href={ route('admin.articles.edit', article_key) } className="block my-2 text-lg text-gray-600 no-underline font-condensed hover:text-blue">
								<Icon icon={ faPencil } className="inline mr-2 svg-fw" />
								Edit This Page
							</a>
						) }
						
						{ admin_content && <div dangerouslySetInnerHTML={ { __html: admin_content } } /> }
						
						<form className="my-4" action={ route('admin.users.search.index') } method="get">
							<input
								name="query"
								className="block w-full px-3 py-1 font-sans text-sm font-normal leading-normal text-gray-700 bg-white border border-gray-300 rounded-full focus:outline-none focus:shadow-outline"
								placeholder="Press ENTER to search"
								autoComplete="off"
							/>
						</form>
					
					</li>
				) }
				<li className="pt-4 my-2 mt-4 text-lg border-t font-condensed">
					{ impersonator_id && (
						<div className="my-2">
							<a href={ route('admin.stop-impersonating') } className="block text-gray-600 no-underline hover:text-blue">
								<Icon icon={ faUserSecret } className="inline mr-2 svg-fw" />
								Stop Viewing as { given_name }
							</a>
						</div>
					) }
					<form action={ route('logout') } method="get">
						<input type="hidden" name="_token" value={ csrf } />
						<button className="block p-0 m-0 text-gray-600 no-underline bg-transparent hover:text-blue" type="submit">
							<Icon icon={ faSignOut } className="inline mr-2 svg-fw" />
							Log Out
						</button>
					</form>
				</li>
			</ul>
		</div>
	);
}

export default hot(AuthPanel);
